import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, BookOpen, Loader2 } from 'lucide-react';
import { api, ApiError, type VibeSpaceResponse } from '@/lib/api';
import { useAuth } from '@/lib/auth-store';

/**
 * Phase-7 Vibe Space — public branded page for an EduBorg org. Lives at
 * `/space/:orgSlug`, no auth required. The page a teacher shares with
 * prospective students.
 *
 * Design notes:
 *   - Deliberately minimal. No AppHeader (this is the public face, not
 *     the authenticated app shell). Just the org's name + tagline +
 *     accent + hero emoji + about + course tiles.
 *   - Accent color drops into a CSS variable so the page lightly
 *     personalizes without us having to ship a real theming engine.
 *     Server-side sanitizer guarantees the value is one of the
 *     allowed shapes (#hex, hsl(...), rgb(...)).
 *   - About-html is server-sanitized via the shared sanitizer (XSS
 *     defense). Render via dangerouslySetInnerHTML on the post-sanitize
 *     content.
 *   - 404 handling: a NotFound for hidden / missing orgs (the API does
 *     the indistinguishable-404 trick).
 *   - Authenticated callers see a "Open the dashboard" link instead of
 *     a "Sign in" link in the top-right; their own course view is
 *     gated by enrollment, not by the public page.
 */
export function VibeSpace() {
  const { orgSlug = '' } = useParams();
  const [data, setData] = useState<VibeSpaceResponse | null>(null);
  const [error, setError] = useState<'not_found' | 'network' | null>(null);
  const [loaded, setLoaded] = useState(false);
  const { state } = useAuth();

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(null);
    api.vibeSpace
      .getPublic(orgSlug)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoaded(true);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) {
          setError('not_found');
        } else {
          setError('network');
        }
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [orgSlug]);

  if (!loaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-accent" aria-hidden />
      </div>
    );
  }

  if (error === 'not_found' || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-6 text-center">
        <p className="font-display text-xl font-semibold">Page not found.</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          We couldn't find a public space at that link. It may have been moved or hidden.
        </p>
        <Link to="/" className="text-sm font-medium text-accent hover:underline">
          Go to EduBorg →
        </Link>
      </div>
    );
  }

  const { org, courses } = data;
  const accent = org.accentColor || 'hsl(218, 92%, 56%)'; // EduBorg default blue
  const hero = org.heroEmoji || '✨';
  const isAuthed = state.status === 'authenticated';

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-[hsl(220,40%,98%)] via-background to-secondary/40"
      style={{ '--vibe-accent': accent } as React.CSSProperties}
    >
      <header className="border-b border-border bg-background/85 backdrop-blur">
        <div className="container flex h-14 items-center justify-between gap-4">
          <Link to="/" className="flex items-center" aria-label="EduBorg home">
            <img
              src="/brand/eduborg-wordmark.png"
              alt="EduBorg"
              width={120}
              height={32}
              className="h-8 w-auto"
            />
          </Link>
          {isAuthed ? (
            <Link
              to="/dashboard"
              className="text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              Dashboard →
            </Link>
          ) : (
            <Link
              to="/auth/sign-in"
              className="text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              Sign in
            </Link>
          )}
        </div>
      </header>

      <main className="container max-w-3xl space-y-10 py-12">
        {/* Optional banner image. Renders above the hero card if the org
            has uploaded one. The hero emoji + logo still show beneath. */}
        {org.bannerR2Key && (
          <section className="-mb-8 overflow-hidden rounded-3xl border border-border shadow-sm">
            <img
              src={api.uploads.objectUrl(org.bannerR2Key)}
              alt=""
              className="h-48 w-full object-cover sm:h-64"
            />
          </section>
        )}

        {/* Hero */}
        <section className="rounded-3xl border border-border bg-card p-8 shadow-sm">
          <div className="flex flex-col items-start gap-4">
            {org.logoR2Key ? (
              <img
                src={api.uploads.objectUrl(org.logoR2Key)}
                alt={`${org.name} logo`}
                className="h-16 w-16 rounded-2xl border border-border object-cover"
              />
            ) : (
              <span
                className="flex h-16 w-16 items-center justify-center rounded-2xl text-4xl"
                style={{ background: `color-mix(in oklab, ${accent} 12%, transparent)` }}
                aria-hidden
              >
                {hero}
              </span>
            )}
            <div className="space-y-2">
              <h1 className="font-display text-3xl font-bold tracking-tight">{org.name}</h1>
              {org.tagline && <p className="text-base text-muted-foreground">{org.tagline}</p>}
            </div>
          </div>
        </section>

        {/* About */}
        {org.aboutHtml && org.aboutHtml.trim() !== '' && (
          <section className="rounded-3xl border border-border bg-card p-8 shadow-sm">
            <h2 className="mb-4 font-display text-lg font-semibold">About</h2>
            {/* Server-side sanitized via sanitizeRichHtml — see
                packages/shared/src/sanitize/html.ts. Safe to render raw. */}
            <div
              className="prose prose-sm max-w-none text-foreground [&_a]:text-accent [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: org.aboutHtml }}
            />
          </section>
        )}

        {/* Courses */}
        <section>
          <header className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">
              {courses.length === 0
                ? 'No public classes yet.'
                : `${courses.length} class${courses.length === 1 ? '' : 'es'} you can join`}
            </h2>
          </header>
          {courses.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
              {org.name} hasn't published any classes yet. Check back soon.
            </p>
          ) : (
            <ul className="space-y-3">
              {courses.map((c) => (
                <li
                  key={c.id}
                  className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="space-y-1">
                    <h3 className="flex items-center gap-2 font-display text-base font-semibold">
                      <BookOpen className="h-4 w-4 text-accent" aria-hidden /> {c.title}
                    </h3>
                    {c.description && (
                      <p className="text-sm text-muted-foreground">{c.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Join code{' '}
                      <span className="font-mono font-semibold text-foreground">{c.joinCode}</span>
                    </p>
                  </div>
                  <Link
                    to={isAuthed ? `/join/${c.joinCode}` : `/auth/sign-up`}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground shadow-sm transition-shadow hover:shadow-md"
                  >
                    Join class <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <footer className="pt-4 text-center text-xs text-muted-foreground">
          Powered by{' '}
          <Link to="/" className="font-medium hover:text-foreground">
            EduBorg
          </Link>
        </footer>
      </main>
    </div>
  );
}
